import type { RoleEnum } from '@zeus/index'
import type { StoredUsers } from '~/gql/users/users'
import { useMutation } from '@apollo/client/react'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { GQL_IMPERSONATE } from '~/gql/auth/auth'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/protected-loader'

type IProps = {
  open: boolean
  onCancel: () => void
  /** Juste de quoi nommer et viser le compte : la liste et la fiche apprenant (#242) l'ouvrent. */
  user: Pick<StoredUsers, 'id' | 'firstName' | 'lastName'>
}

function UserImpersonateModal({ open, onCancel, user }: IProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setPayload } = useAuth()

  const [impersonateUser] = useMutation(GQL_IMPERSONATE)

  const name = `${user.firstName} ${user.lastName}`

  const onImpersonate = async () => {
    try {
      const { data } = await impersonateUser({ variables: { userId: user.id } })
      if (data?.impersonateUser.__typename === 'UserInfo' && data.impersonateUser) {
        toast.success(t('impersonate.started', { name }))
        setPayload({
          firstName: data.impersonateUser.firstName,
          lastName: data.impersonateUser.lastName,
          email: data.impersonateUser.email,
          username: data.impersonateUser.username,
          role: data.impersonateUser.roles,
          id: data.impersonateUser.id,
          impersonatorId: data.impersonateUser.impersonatorId,
        })

        navigate(getDefaultRoute(data.impersonateUser.roles as RoleEnum))
      } else {
        toast.error(t('bad.impersonate.user'))
      }
    } catch {
      toast.error(t('bad.impersonate.user'))
      onCancel()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('impersonate.user', { name })}</DialogTitle>
          {/*
            asChild : DialogDescription rend un <p>, on ne peut donc pas y
            imbriquer les paragraphes du corps sans produire du HTML invalide.
          */}
          <DialogDescription asChild>
            <div className="space-y-3 text-left">
              <p>{t('impersonate.user.preview', { name })}</p>
              {/*
                L'impersonation n'est PAS en lecture seule : seul le tracking
                SCORM est neutralisé (cf. scorm-tracking.service). L'avertissement
                doit rester visible pour que l'admin ne clique pas à l'aveugle.
              */}
              <p className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                <span>{t('impersonate.user.not-read-only')}</span>
              </p>
              <p>{t('impersonate.user.return-hint')}</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onImpersonate}>
            {t('impersonate.user.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UserImpersonateModal
